import ContactList from 'components/ContactList/ContactList';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import duckify, { filterDuck } from 'utils/duckify';
import withSorting from 'hocs/withSorting';
import { compose } from 'redux';
import { formInboxItems, grades, schoolYears } from 'ducks';
import { formatDate } from 'utils';
import { withRouter } from 'react-router';
import useBulkActions from 'hooks/useBulkActions';
import withFiltering from 'hocs/withFiltering';
import Filters from 'components/Filters/Filters';
import FontAwesome from 'react-fontawesome';

import './styles/FormInbox.scss';

/** Renders a table of Form Inbox Items. */
const FormInboxList = ({
  view,
  userType,

  // mapDucksToProps
  items,
  grades,
  schoolYears,

  // withFiltering
  filters,
  setFilters,

  // withSorting
  sort,
  onSort
}) => {

  const {
    itemIsSelected,
    promptSelectAll,
    selectionMode,
    selectionModeCount,
    togglePageTracked,
    toggleTrackedItem,
    trackedItems,
  } = useBulkActions(items);

  const isSelectAll = selectionMode === 'all' || promptSelectAll;
  const isSelectNone = selectionMode !== 'all' && !trackedItems.length;

  useEffect(() => {
    // Call legacy jQuery helper to toggle bulk actions since they have not been migrated to react yet
    global.Header.toggleBulkActions(isSelectNone);
  }, [isSelectNone]);

  const selectColumn = {
    header: () => <input
      type="checkbox"
      className="contact-list-react-header-checkbox bulk-toggle"
      checked={isSelectAll}
      onChange={togglePageTracked}
    />,
    value: (item) => <td>
      <input
        id={`resource_${item.id}`}
        type="checkbox"
        className="bulk-item"
        name={`ids[${item.display_type}][]`}
        value={item.id}
        data-owner-id={item.bulk_email_permitted ? item.owner_id : null }
        data-owner-email={item.bulk_email_permitted ? item.owner_email : null }
        data-owner-name={item.bulk_email_permitted ? item.owner_name : null }
        checked={itemIsSelected(item.id)}
        onChange={toggleTrackedItem}
      />
    </td>
  };

  const actionColumn = {
    header: 'Submit Form',
    value: item => <td>
      {userType === 'admin' && <a className="icon" href={`/applications/edit/${item.id}/${item.last_page || 0}`}>
        <FontAwesome name="pencil"/>
      </a>}

      {userType === 'parent' && item.status === 'Pending payment' && <a
        data-confirm="Please confirm that you wish to submit this application and bypass the online payment process. Once submitted, this action cannot be undone."
        rel="nofollow"
        data-method="post"
        href={`/applications/${item.id}/force_submit_application`}>
        <FontAwesome name="download"/> Submit Form
      </a>}
    </td>
  };

  const columns = [selectColumn, ...dataColumns, actionColumn];

  const filterFields = [
    {
      solr_prompt: 'aasm_state',
      display_name: 'Status',
      options: [['Draft', 'draft'], ['Pending Payment', 'pending_payment']]
    },
    {
      solr_prompt: 'child_apply_year',
      display_name: 'Term',
      options: schoolYears.map(y => [y.name, y.id])
    },
    {
      solr_prompt: 'child_apply_grade',
      display_name: 'Grade',
      options: grades.map(g => [g.name, g.id])
    }
  ];

  return <React.Fragment>
    <div className="form-inbox list">
      <Filters
        count={items.length}
        fields={filterFields}
        selected={filters}
        selectCount={selectionModeCount()}
        submitFilters={setFilters}
        itemsName="forms"
      />

      <ContactList
        className="form-list table-bulk"
        columns={columns}
        contacts={items}
        emptyMessage="No forms"
        sort={sort}
        onSort={onSort}
      />
    </div>
  </React.Fragment>;
};

const dataColumns = [
  {
    header: 'Name on Form',
    value: item => <td><a href={`/admin/applications/${item.id}`}>{item.display_name}</a></td>,
    sortName: 'display_name',
  },
  {
    header: 'Form Type',
    value: 'display_type',
    sortName: 'display_type',
  },
  {
    header: 'Status',
    value: 'status',
    sortName: 'status',
  },
  {
    header: 'Start Date',
    value: item => <td>{formatDate(item.created_at)}</td>,
    sortName: 'created_at'
  },
  {
    header: 'Last Updated Date',
    value: item => <td>{item.last_updated ? formatDate(item.last_updated) : null}</td>,
    sortName: 'last_updated'
  },
  {
    header: 'Apply Grade',
    value: 'grade_name',
    sortName: 'grade_num'
  },
  {
    header: 'Apply Year',
    value: 'school_year_name',
    sortName: 'school_year_num'
  },
  {
    header: 'User Name',
    value: 'owner_name',
    sortName: 'owner_name'
  },
  {
    header: 'User Email',
    value: 'owner_email',
    sortName: 'owner_email'
  }
];

FormInboxList.propTypes = {
  userType: PropTypes.oneOf(['admin', 'parent']),
  view: PropTypes.oneOf(['drafts']),

  // mapDucksToProps
  items: PropTypes.array,
  grades: PropTypes.array,
  schoolYears: PropTypes.array,
};

const mapDucksToProps = ({ view, userType, filters, sort }) => ({
  items: filterDuck(formInboxItems, { view: view, user_type: userType, filters, sort }),
  grades,
  schoolYears,
});

const mapStateToProps = () => ({});
const mapDispatchToProps = () => ({});

export default compose(
  withRouter,
  withFiltering,
  withSorting({ column: dataColumns[0].sortName, order: 'asc' }),
  duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)
)(FormInboxList);
